/**
 * @file Streets.repository.ts
 * @module StreetsRepository
 * @description Reaktīvā stāvokļa krātuve Latvijas ceļu tīkla datiem (ielu/ceļu saraksts),
 *              izmantojot @ngneat/elf. Nodrošina getStreetItems() novērojamo straumi
 *              un updateStreetItems() mutāciju lineārās references automātiskajam pabeigšanai.
 *              Dati nāk no LVC ceļu tīkla (CAPS mikroceļu tīkls).
 */

import { createStore, setProp, withProps } from "@ngneat/elf";
import { withEntities, selectAllEntities, upsertEntities } from "@ngneat/elf-entities";

/** Response from the linear referencing API for a single route segment */
/** Response from the linear referencing API for a single route segment */
export interface LinearRefResponse {
	/** Route identifier (e.g., "A2", "P4") */
	route_id: string;
	/** Start point coordinates in EPSG:3059 */
	from_point: Point;
	/** End point coordinates in EPSG:3059 */
	to_point: Point;
	/** Kilometer measure at the queried point */
	measure: string;
	/** WKT point geometry at the queried location */
	point: string;
	/** Associated address records */
	addresses: any[];
	/** Start kilometer of the segment */
	from_measure: number;
	/** End kilometer of the segment */
	to_measure: number;
	/** WKT geometry of the route segment in EPSG:3059 */
	geometry_wkt: string;
	/** OpenLR location reference (for cross-system interoperability) */
	openlr: any;
}

/** 2D point coordinates */
export interface Point {
	x: number;
	y: number;
}

/** Store properties for pagination state */
export interface StreetProps {
	/** URL for the next page of results (null when all pages loaded) */
	next?: string;
}

/** Internal entity shape for the Elf entity store (keyed by routeid) */
interface StreetEntities {
	/** Route identifier (primary key, e.g., "A2") */
	routeid: string;
	/** Minimum kilometer value on this route */
	min_measure?: number;
	/** Maximum kilometer value on this route */
	max_measure?: number;
}

/** Paginated response from the streets/routes API */
export interface StreetResponse {
	/** Total number of routes */
	count: number;
	/** URL for the next page of results */
	next: string;
	/** URL for the previous page (null for first page) */
	previous: null;
	/** Route records for this page */
	results: Result[];
}

/** Individual route record from the streets API */
export interface Result {
	/** Route identifier (e.g., "A2", "V123") */
	routeid: string;
	/** Minimum kilometer value on this route */
	min_measure?: number;
	/** Maximum kilometer value on this route */
	max_measure?: number;
}

const STORE = createStore({ name: "streets" }, withEntities<StreetEntities, "routeid">({ idKey: "routeid" }), withProps<StreetProps>({}));

/** Returns an observable of all loaded route entities */
export const getStreetItems = () => {
	return STORE.pipe(selectAllEntities());
};

/** Upserts route entities and updates the pagination cursor */
export const updateStreetItems = (results: Result[], next: string) => {
	STORE.update(upsertEntities(results), setProp("next", next));
};
